//#region IMPORTS
import { ORDERSTATUS, ORDERTYPE, Order } from "../../models/order";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { Fragment, useState } from "react";
import { useAppSelector } from "../../store/configureStore";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import OrdersTableRowActionApproveToggle from "./OrdersTableRowActionApproveToggle";
import OrdersTableRowActionApprove from "./OrdersTableRowActionApprove";
import OrdersTableRowActionCancel from "./OrdersTableRowActionCancel";
import OrdersTableRowActionRead from "./OrdersTableRowActionRead";
import OrdersTableRowActionShip from "./OrdersTableRowActionShip";
import OrdersTableRowActionPicklist from "./OrdersTableRowActionPicklist";
import OrdersTableRowActionInProgress from "./OrdersTableRowActionInProgress";
import OrdersTableRowCheckbox from "./OrdersTableRowCheckbox";
import useShowRowActions from "../../hooks/useShowRowActions";
import useBulkActionsAvailable from "../../hooks/useBulkActionsAvailable";
import OrdersTableRowSubOrder from "./OrdersTableRowSubOrder";
import OrderStatusControl from "./OrderStatusControl";
import OrdersTableRowItems from "./OrdersTableRowItems";
import OrderTypeBadge from "./OrderTypeBadge";
//#endregion

//#region INTERFACE
interface Props {
  clearSelectedOrdersHandler: () => void;
  changeOrderStatusHandler: (order: Order, status: string) => void;
  downloadPicklistHandler?: (order: Order) => void;
  selectOrderHandler: (e: any, value: Order) => void;
  selectedOrders: Order[];
  isHQTeam?: boolean;
  isPicker?: boolean;
  isAdmin?: boolean;
  pageHasApprovable?: boolean;
  order: Order;
}
//#endregion

export default function OrdersTableRow({
  clearSelectedOrdersHandler,
  changeOrderStatusHandler,
  downloadPicklistHandler,
  selectOrderHandler,
  selectedOrders = [],
  pageHasApprovable = false,
  isHQTeam = false,
  isPicker = false,
  isAdmin = false,
  order,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const { showOrdersWithSuborders, filters, order_params } = useAppSelector(
    (state) => state.orders,
  );
  const isInProgressFilter =
    order_params.order_status === ORDERSTATUS.inProgress;
  // Decline only applies before approval — hide it from In progress onwards.
  const isCancelledFilter =
    order_params.order_status === ORDERSTATUS.cancelled;
  const hideDeclineColumn =
    isInProgressFilter ||
    order_params.order_status === ORDERSTATUS.shipped ||
    isCancelledFilter;

  const showRowActions = useShowRowActions();
  const bulkAvailable = useBulkActionsAvailable();

  // Local state
  const [showThisOrderParcelInfo, setShowThisOrderParcelInfo] =
    useState<boolean>(false);
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      <tr className="border-t border-slate-300 h-10">
        {/* Checkbox */}
        {(isAdmin || isPicker || isHQTeam) && bulkAvailable && (
          <td className="pr-2 text-left text-xs text-gray-500 sm:pl-0">
            {order.promo_id &&
              order.order_type !== ORDERTYPE.original &&
              order.order_status !== ORDERSTATUS.cancelled && (
                <OrdersTableRowCheckbox
                  selectOrderHandler={(e: any) => selectOrderHandler(e, order)}
                  selectedOrders={selectedOrders}
                  order={order}
                />
              )}
          </td>
        )}

        {/* Actions */}
        <td className="pr-2 w-8 text-left text-sm text-gray-500 sm:pl-0">
          <OrdersTableRowActionRead order={order} />
        </td>

        {/* Store.store_name */}
        {isHQTeam && (
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
            {order.store ? order.store.store_name : "No store found"}
          </td>
        )}

        {/* Store.external_store_id */}
        {(isAdmin || isPicker || isHQTeam) && (
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
            {order.store ? order.store.external_store_id : "No store id found"}
          </td>
        )}

        {/* Project */}
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {order.promo_name}
        </td>

        {/* Order date */}
        <td className="whitespace-nowrap px-2 pr-4 text-left text-xs text-gray-500 sm:pl-0">
          {order.order_date}
        </td>

        {/* Order code */}
        {!isHQTeam && (
          <td className="whitespace-nowrap px-2 pr-4 text-left text-xs text-gray-500 sm:pl-0">
            {order.order_code}
          </td>
        )}

        {/* Order type */}
        <td className="whitespace-nowrap px-2 pr-4 text-left text-xs text-gray-500 sm:pl-0">
          <OrderTypeBadge orderTypeName={order.order_type} />
        </td>

        {/* Order status */}
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          <OrderStatusControl
            currentStatus={order.order_status}
            editable={
              isAdmin &&
              order.order_type !== ORDERTYPE.original &&
              order.order_status !== ORDERSTATUS.shipped
            }
            orderStatusses={filters.order_status}
            changeOrderStatusHandler={(status: string) =>
              changeOrderStatusHandler(order, status)
            }
          />
        </td>

        {/* Items */}
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {!showThisOrderParcelInfo && (
            <OrdersTableRowItems
              order={order}
              showByPrinter={isHQTeam || isAdmin}
            />
          )}
        </td>

        {/* Comment */}
        <td className="whitespace-nowrapmax-w-24 px-2 text-left text-sm text-gray-500 sm:pl-0">
          {order.comment !== null &&
            order.order_type === ORDERTYPE.backorder &&
            order.comment !== "no comment" && (
              <Fragment>
                <ChatBubbleLeftIcon
                  className="w-6"
                  data-tooltip-id={order.id.toString()}
                  data-tooltip-content={order.comment}
                />
                <Tooltip id={order.id.toString()} />
              </Fragment>
            )}
        </td>

        {/* Parcel info */}
        <td className="px-2 text-left text-xs text-gray-500 sm:pl-0">
          {!showOrdersWithSuborders && (
            <Fragment>
              {order.sub_orders && order.sub_orders.length > 0 ? (
                <button
                  className="flex items-center gap-2"
                  onClick={() => setShowThisOrderParcelInfo((prev) => !prev)}
                >
                  {showThisOrderParcelInfo ? (
                    <EyeSlashIcon height={16} className="text-red-600" />
                  ) : (
                    <EyeIcon height={16} />
                  )}
                  {order.sub_orders.length}{" "}
                  {order.sub_orders.length > 1
                    ? t("rituals.parcels")
                    : t("rituals.parcel")}
                </button>
              ) : (
                // fallback when no suborders
                "No parcel info"
              )}
            </Fragment>
          )}
        </td>

        {/* Reasons */}
        {isHQTeam && (
          <td className="whitespace-nowrap px-2 py-1 text-left text-[10px] text-gray-500 sm:pl-0">
            <div className="flex flex-wrap gap-1">
              {order.order_type === ORDERTYPE.backorder &&
                [
                  ...new Set(
                    order.order_items.map(
                      (orderItem) => orderItem.basket_reason,
                    ),
                  ),
                ]
                  .sort()
                  .map((value: string) => (
                    <span
                      key={value}
                      className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-800 ring-1 ring-inset ring-slate-800/10"
                    >
                      {t(`rituals.${value.toLowerCase()}`)}
                    </span>
                  ))}
            </div>
          </td>
        )}

        {/* Decline — promo approve/decline is HQ / admin only (not pickers) */}
        {showRowActions &&
          !hideDeclineColumn &&
          pageHasApprovable &&
          (isHQTeam || isAdmin) && (
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900 sm:pl-0">
            <OrdersTableRowActionApproveToggle
              order={order}
              clearSelectedOrdersHandler={clearSelectedOrdersHandler}
              hide={
                !(
                  (isHQTeam || isAdmin) &&
                  order.order_type !== ORDERTYPE.original &&
                  order.order_status === ORDERSTATUS.pending
                )
              }
            />
          </td>
        )}

        {showRowActions && !isCancelledFilter && (
          <td className="whitespace-nowrap px-2 text-left text-sm text-gray-900 sm:pl-0">
            <div className="flex gap-2 items-center">
              {/* Request list */}
            <OrdersTableRowActionPicklist
              picklistHandler={() => downloadPicklistHandler?.(order)}
              order={order}
              hide={
                !downloadPicklistHandler ||
                !(
                  (isPicker || isAdmin) &&
                  order.order_type !== ORDERTYPE.original &&
                  order.order_status !== ORDERSTATUS.cancelled
                )
              }
            />

              {/* Approve order — HQ / admin only (not pickers) */}
            <OrdersTableRowActionApprove
              approveHandler={() =>
                changeOrderStatusHandler(order, ORDERSTATUS.ordered)
              }
              order={order}
              hide={
                !(
                  (isHQTeam || isAdmin) &&
                  order.order_type !== ORDERTYPE.original &&
                  order.order_status === ORDERSTATUS.pending
                )
              }
            />

            {/* Set in progress */}
            <OrdersTableRowActionInProgress
              inProgressHandler={() =>
                changeOrderStatusHandler(order, ORDERSTATUS.inProgress)
              }
              order={order}
              hide={
                isInProgressFilter ||
                !(
                  (isPicker || isAdmin) &&
                  order.order_type !== ORDERTYPE.original &&
                  order.order_status === ORDERSTATUS.ordered
                )
              }
            />

            {/* Ship order */}
            <OrdersTableRowActionShip
              shipHandler={() =>
                changeOrderStatusHandler(order, ORDERSTATUS.shipped)
              }
              order={order}
              hide={
                !(
                  (isPicker || isAdmin) &&
                  order.order_type !== ORDERTYPE.original &&
                  order.order_status === ORDERSTATUS.inProgress
                )
              }
            />

            {/* Cancel order */}
            <OrdersTableRowActionCancel
              cancelHandler={() =>
                changeOrderStatusHandler(order, ORDERSTATUS.cancelled)
              }
              order={order}
              hide={
                !(
                  ((isAdmin || isPicker) &&
                    order.order_type !== ORDERTYPE.original &&
                    order.order_status !== ORDERSTATUS.cancelled &&
                    order.order_status !== ORDERSTATUS.shipped) ||
                  (isHQTeam &&
                    order.order_type !== ORDERTYPE.original &&
                    order.order_status !== ORDERSTATUS.cancelled &&
                    order.order_status !== ORDERSTATUS.shipped &&
                    order.order_status !== ORDERSTATUS.ordered)
                )
              }
            />

            </div>
          </td>
        )}
      </tr>

      {/* Sub orders extra rows */}
      {order.sub_orders &&
        order.sub_orders.map(
          (subOrder) =>
            (showOrdersWithSuborders || showThisOrderParcelInfo) && (
              <OrdersTableRowSubOrder
                isPicker={isPicker}
                isHQTeam={isHQTeam}
                subOrder={subOrder}
                isAdmin={isAdmin}
                key={subOrder.id}
                order={order}
              />
            ),
        )}
    </Fragment>
  );
  //#endregion
}
